import React, { useContext, useState, FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../context/AuthContext";
import LanguageSwitcher from "../components/ui/LanguageSwitcher";
import { getTranslatedError } from "../utils/translateError";

const LoginPage: React.FC = () => {
	const { t } = useTranslation();
	const authContext = useContext(AuthContext);
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	if (!authContext) {
		throw new Error("LoginPage must be used within AuthProvider");
	}

	const { isAuthenticated, loading, login } = authContext;

	if (!loading && isAuthenticated) {
		return <Navigate to="/report" replace />;
	}

	if (loading && !isSubmitting) {
		return (
			<div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800">
				<div className="text-center">
					<div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-primary-200 border-t-primary-400 mb-4"></div>
					<div className="text-gray-600 font-medium">{t("common.loading")}</div>
				</div>
			</div>
		);
	}

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);
		setIsSubmitting(true);

		try {
			await login(username, password);
		} catch (err) {
			setError(getTranslatedError(err, "errors.loginFailed"));
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 px-4">
			<div className="absolute top-4 right-4">
				<LanguageSwitcher />
			</div>
			<div className="w-full max-w-md">
				<div className="bg-white shadow-soft-lg rounded-2xl px-6 sm:px-8 pt-10 pb-10 mb-4 border border-gray-200">
					<div className="text-center mb-8">
						<h1 className="text-3xl font-bold text-gradient mb-2">
							{t("login.title")}
						</h1>
						<p className="text-gray-600 text-sm">{t("login.subtitle")}</p>
					</div>

					<form onSubmit={handleSubmit}>
						<div className="mb-5">
							<label
								htmlFor="username"
								className="block text-gray-700 text-sm font-semibold mb-2"
							>
								{t("login.usernameLabel")}
							</label>
							<input
								id="username"
								type="text"
								value={username}
								onChange={(e) => setUsername(e.target.value)}
								required
								disabled={isSubmitting}
								className="appearance-none border rounded-lg w-full py-2.5 px-4 text-gray-700 caret-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed transition-[box-shadow,border-color] duration-200 bg-white shadow-sm hover:shadow-md border-gray-300"
								placeholder={t("login.usernamePlaceholder")}
							/>
						</div>

						<div className="mb-6">
							<label
								htmlFor="password"
								className="block text-gray-700 text-sm font-semibold mb-2"
							>
								{t("login.passwordLabel")}
							</label>
							<div className="relative">
								<input
									id="password"
									type={showPassword ? "text" : "password"}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									required
									disabled={isSubmitting}
									className="appearance-none border rounded-lg w-full py-2.5 pl-4 pr-16 text-gray-700 caret-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed transition-[box-shadow,border-color] duration-200 bg-white shadow-sm hover:shadow-md border-gray-300"
									placeholder={t("login.passwordPlaceholder")}
								/>
								<button
									type="button"
									onClick={() => setShowPassword((current) => !current)}
									disabled={isSubmitting}
									className="absolute inset-y-0 right-0 flex items-center px-4 text-sm font-semibold text-primary-500 hover:text-primary-600 focus:outline-none disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
									aria-label={
										showPassword
											? t("login.hidePassword")
											: t("login.showPassword")
									}
								>
									{showPassword ? t("login.hide") : t("login.show")}
								</button>
							</div>
						</div>

						{error && (
							<div className="mb-4 p-4 bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 text-red-700 rounded-lg shadow-sm">
								<div className="flex items-center">
									<svg
										className="w-5 h-5 mr-2 flex-shrink-0"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fillRule="evenodd"
											d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
											clipRule="evenodd"
										/>
									</svg>
									<span className="font-semibold">{error}</span>
								</div>
							</div>
						)}

						<div className="flex items-center justify-between">
							<button
								type="submit"
								disabled={isSubmitting}
								className="bg-gradient-to-r from-primary-400 to-primary-500 hover:from-primary-500 hover:to-primary-600 text-white font-semibold py-3 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed w-full shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
							>
								{isSubmitting ? (
									<span className="flex items-center justify-center">
										<svg
											className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
										>
											<circle
												className="opacity-25"
												cx="12"
												cy="12"
												r="10"
												stroke="currentColor"
												strokeWidth="4"
											></circle>
											<path
												className="opacity-75"
												fill="currentColor"
												d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
											></path>
										</svg>
										{t("login.submitting")}
									</span>
								) : (
									t("login.submitButton")
								)}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
};

export default LoginPage;
